import dayjs from 'dayjs';
import z from 'zod';
import { enumLabels } from '../common/fields';
import { HumanFullNameSchema, StringBooleanSchema, UnixTimestampSchema } from '../common/schemas';
import { UserIdSchema } from '../users/schemas';
import {
  UserNoteAssignee,
  UserNoteCreator,
  UserNoteCreatorDisplayName,
  UserNoteType,
} from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBUserNoteTypeSchema = z.enum(UserNoteType);

/**
 * DB Schemas
 */

export const DBUserNoteUserIdSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    DBUserNoteTypeSchema.safeParse(parts[0]).success &&
    z.lazy(() => UserIdSchema).safeParse(parts[1]).success
  );
});
export const DBUserNoteCreationDatetimeSchema = UnixTimestampSchema;
export const DBUserNoteActiveSchema = StringBooleanSchema;
export const DBUserNoteActiveBillingRelatedKeySchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    StringBooleanSchema.safeParse(parts[0]).success &&
    StringBooleanSchema.safeParse(parts[1]).success
  );
});
export const DBUserNoteAssigneeSchema = z.union([
  z.enum(UserNoteAssignee),
  z.lazy(() => UserIdSchema),
]);
export const DBUserNoteAssigneeNameSchema = z.union([z.literal(''), HumanFullNameSchema]);
export const DBUserNoteBillingRelatedSchema = z.boolean();
export const DBUserNoteCreatorSchema = z.union([
  z.enum(UserNoteCreator),
  z.lazy(() => UserIdSchema),
]);
export const DBUserNoteCreatorNameSchema = z.union([
  z.string().min(1).max(256),
  HumanFullNameSchema,
]);
export const DBUserNoteDueDatetimeSchema = UnixTimestampSchema;
export const DBUserNoteLastEditDatetimeSchema = UnixTimestampSchema;
export const DBUserNoteNoteSchema = z.string().min(1).max(4096);
export const DBUserNotePriorityDatetimeSchema = UnixTimestampSchema;

export const DBUserNoteSchema = z.object({
  userId: DBUserNoteUserIdSchema,
  creationDatetime: DBUserNoteCreationDatetimeSchema,
  active: DBUserNoteActiveSchema,
  activeBillingRelatedKey: DBUserNoteActiveBillingRelatedKeySchema,
  assignee: DBUserNoteAssigneeSchema,
  assigneeName: DBUserNoteAssigneeNameSchema,
  billingRelated: DBUserNoteBillingRelatedSchema,
  creator: DBUserNoteCreatorSchema,
  creatorName: DBUserNoteCreatorNameSchema,
  dueDatetime: DBUserNoteDueDatetimeSchema,
  lastEditDatetime: DBUserNoteLastEditDatetimeSchema,
  note: DBUserNoteNoteSchema,
  priorityDatetime: DBUserNotePriorityDatetimeSchema,
});

/**
 * Client Schemas
 */

export const UserNoteIdSchema = z.string().brand<'UserNoteId'>();
export const UserNoteOwnerIdSchema = z.lazy(() => UserIdSchema);
export const UserNoteTypeSchema = DBUserNoteTypeSchema;
export const UserNoteCreationDatetimeSchema = DBUserNoteCreationDatetimeSchema;
export const UserNoteActiveSchema = z.boolean();
export const UserNoteAssigneeIdSchema = z.lazy(() => UserIdSchema);
export const UserNoteAssigneeNameSchema = HumanFullNameSchema;
export const UserNoteBillingRelatedSchema = DBUserNoteBillingRelatedSchema;
export const UserNoteCreatedBySchema = z.lazy(() => UserIdSchema);
export const UserNoteCreatedBySystemSchema = z
  .enum(UserNoteCreator)
  .register(enumLabels, { labels: UserNoteCreatorDisplayName });
export const UserNoteCreatorNameSchema = DBUserNoteCreatorNameSchema;
export const UserNoteDueDatetimeSchema = DBUserNoteDueDatetimeSchema;
export const UserNoteLastEditDatetimeSchema = DBUserNoteLastEditDatetimeSchema;
export const UserNoteNoteSchema = DBUserNoteNoteSchema;
export const UserNotePriorityDatetimeSchema = DBUserNotePriorityDatetimeSchema;

export const UserNoteSchema = z.object({
  id: UserNoteIdSchema.meta({ title: 'Note ID' }),
  ownerId: UserNoteOwnerIdSchema.meta({ title: 'Owner ID' }),
  type: UserNoteTypeSchema,
  creationDatetime: UserNoteCreationDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Created at',
  }),
  active: UserNoteActiveSchema.default(true).meta({ title: 'Active' }),
  // Both absent while unassigned; a write of a null assigneeId unassigns the note
  assigneeId: UserNoteAssigneeIdSchema.nullish().meta({ title: 'Assignee' }),
  assigneeName: UserNoteAssigneeNameSchema.nullish().meta({ title: 'Assignee name' }),
  billingRelated: UserNoteBillingRelatedSchema.default(false).meta({ title: 'Billing related' }),
  // A note is created by a user or by a system, never both
  createdBy: UserNoteCreatedBySchema.optional().meta({ title: 'Created by' }),
  createdBySystem: UserNoteCreatedBySystemSchema.optional().meta({
    title: 'Created by system',
  }),
  creatorName: UserNoteCreatorNameSchema.meta({ title: 'Creator name' }),
  // Absent while unset; a write of null clears it
  dueDatetime: UserNoteDueDatetimeSchema.nullish().meta({ title: 'Due at' }),
  lastEditDatetime: UserNoteLastEditDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Last edited at',
  }),
  note: UserNoteNoteSchema.meta({ title: 'Note' }),
  priorityDatetime: UserNotePriorityDatetimeSchema.nullish().meta({ title: 'Prioritised at' }),
});
